import type {
  InfiniteData,
  UseInfiniteQueryResult,
} from '@tanstack/react-query'
import { useInfiniteQuery } from '@tanstack/react-query'

import { fetchMultisigProposals, MultisigProposalsResponse } from '@/api'
import { buildBasePagination } from '@/helpers/pagination'
import { queryKeys } from '@/queries/keys'
import { PaginationParams } from '@/types'

interface QueryOptions {
  enabled?: boolean
  staleTime?: number
  refetchInterval?: number
}

interface UseMultisigProposalsInfiniteListOptions {
  pagination?: PaginationParams
  queryOptions?: QueryOptions
}

export function useMultisigProposalsInfiniteList(
  options?: UseMultisigProposalsInfiniteListOptions,
) {
  const basePagination = buildBasePagination(options?.pagination)
  type MultisigProposalsInfiniteData = InfiniteData<
    MultisigProposalsResponse,
    string | undefined
  >

  return useInfiniteQuery<
    MultisigProposalsResponse,
    unknown,
    MultisigProposalsInfiniteData,
    ReturnType<typeof queryKeys.multisig.proposalsList>,
    string | undefined
  >({
    queryKey: queryKeys.multisig.proposalsList(basePagination),
    initialPageParam: options?.pagination?.key,
    queryFn: ({ pageParam }) =>
      fetchMultisigProposals({
        ...basePagination,
        key: pageParam,
        reverse: true,
      }),
    getNextPageParam: lastPage => lastPage.pagination.next_key ?? undefined,
    enabled: options?.queryOptions?.enabled ?? true,
    staleTime: options?.queryOptions?.staleTime,
    refetchInterval: options?.queryOptions?.refetchInterval,
  }) as UseInfiniteQueryResult<MultisigProposalsInfiniteData, unknown>
}
